import { useRef, useState } from 'react';
import {
  UPLOAD_TARGETS, acceptFor, formatBytes,
  type UploadTargetName, type UploadResult,
} from 'shared';
import { api, ApiError } from '../lib/api';
import { Icon } from './icons';

interface Props {
  target: UploadTargetName;
  /** The stored value: a public URL, or '' when nothing is attached yet. */
  value: string;
  onChange: (url: string) => void;
  id: string;
  /** Shown under the control. */
  hint?: string;
}

/**
 * Attach a file to a record.
 *
 * The value is and stays a plain URL, so a document already hosted elsewhere can still be
 * pasted — which is how every existing record got its letter, and those must keep working.
 * Uploading is the easy path, not the only one.
 */
export function FileUpload({ target, value, onChange, id, hint }: Props) {
  const spec = UPLOAD_TARGETS[target];
  const input = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attached, setAttached] = useState<UploadResult | null>(null);

  async function pick(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.upload<UploadResult>(`/uploads/${target}`, file);
      setAttached(res);
      onChange(res.url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Gagal mengunggah berkas.');
    } finally {
      setBusy(false);
      // Clear the picker so choosing the SAME file again still fires a change event.
      if (input.current) input.current.value = '';
    }
  }

  return (
    <div className="stack" style={{ gap: '0.45rem' }}>
      <div className="upload-row">
        <input
          id={id}
          value={value}
          placeholder="Tempel URL, atau unggah berkas"
          onChange={(e) => { onChange(e.target.value); setAttached(null); }}
        />
        <button type="button" onClick={() => input.current?.click()} disabled={busy}>
          <Icon name="upload" size={16} />
          {busy ? 'Mengunggah…' : 'Unggah'}
        </button>
        <input
          ref={input}
          type="file"
          accept={acceptFor(target)}
          hidden
          onChange={(e) => void pick(e.target.files?.[0])}
        />
      </div>

      {error && <div className="msg error">{error}</div>}

      {attached && (
        <span className="muted" style={{ fontSize: 12.5 }}>
          Terunggah · {attached.contentType} · {formatBytes(attached.sizeBytes)}
        </span>
      )}

      {value && !error && (
        <a href={value} target="_blank" rel="noreferrer noopener" style={{ fontSize: 12.5 }}>
          Buka berkas
        </a>
      )}

      <span className="muted" style={{ fontSize: 12.5 }}>
        {hint ? `${hint} ` : ''}
        Maksimal {formatBytes(spec.maxBytes)} · {spec.contentTypes.join(', ')}.
      </span>
    </div>
  );
}
